import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui";
import { useAuth } from "@/features/auth/AuthContext";
import { ThemeToggle } from "./ThemeContext";
import { BrandMark, BrandWordmark } from "./components/BrandMark";
import { cn } from "@/lib/utils/cn";
import { canAnywhere, isOrgWide } from "@/lib/utils/permissions";
import type { EventPermission, User } from "@/lib/types/api";
import {
  IconDashboard, IconGallery, IconCamera, IconPrinter, IconUsers, IconSettings, IconSparkles,
} from "./components/icons";

type NavNeed = EventPermission | "orgWide";
const NAV = [
  { to: "/admin", label: "Overview", end: true, icon: IconDashboard },
  { to: "/admin/gallery", label: "AI Gallery", needs: "gallery", icon: IconGallery },
  { to: "/admin/snapshot-gallery", label: "Booth photos", needs: "gallery", icon: IconCamera },
  { to: "/admin/revenue", label: "Revenue", needs: "revenue", icon: IconSparkles },
  { to: "/admin/print-queue", label: "Print queue", needs: "prints", icon: IconPrinter },
  { to: "/admin/team", label: "Team", needs: "orgWide", icon: IconUsers },
  { to: "/admin/organization", label: "Organization", needs: "orgWide", icon: IconSettings },
] as Array<{ to: string; label: string; end?: boolean; needs?: NavNeed; icon: typeof IconDashboard }>;

function visibleNav(user: User | null) {
  return NAV.filter((item) => {
    if (!item.needs) return true;
    if (item.needs === "orgWide") return isOrgWide(user);
    return canAnywhere(user, item.needs);
  });
}

export function AdminLayout() {
  const { user, organization, logout } = useAuth();
  const navigate = useNavigate();
  const nav = visibleNav(user);

  async function onLogout() {
    await logout();
    navigate("/admin/login", { replace: true });
  }

  return (
    <div className="min-h-full bg-ink-850 text-haze-100 lg:flex">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[258px] flex-col border-r border-ink-600 bg-ink-800/90 px-4 py-5 backdrop-blur-xl lg:flex">
        <NavLink to="/admin" className="mb-9 flex h-10 items-center px-2" aria-label="JebaXP admin home">
          <BrandWordmark className="h-7" />
        </NavLink>

        <p className="label-eyebrow mb-3 px-3">Workspace</p>
        <nav className="flex flex-col gap-1" aria-label="Main navigation">
          {nav.map((item) => {
            const Icon = item.icon;
            return <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) => cn(
                "group flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium transition-colors",
                isActive
                  ? "bg-accent/10 text-accent ring-1 ring-inset ring-accent/20"
                  : "text-haze-300 hover:bg-ink-700 hover:text-haze-100",
              )}>
              <Icon size={17} className="shrink-0 opacity-80 group-hover:opacity-100" />
              {item.label}
            </NavLink>;
          })}
        </nav>

        <div className="mt-auto flex flex-col gap-4">
          <div className="rounded-2xl border border-ink-600 bg-ink-750 p-3.5">
            <div className="flex items-center gap-2.5">
              <span className="grid h-8 w-8 place-items-center rounded-xl bg-accent/10"><BrandMark size={24} /></span>
              <span className="min-w-0">
                <span className="block truncate text-xs font-semibold text-haze-100">{organization?.name ?? "Jeba workspace"}</span>
                <span className="mt-0.5 block text-2xs text-haze-400">Admin workspace</span>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 border-t border-ink-600 pt-4">
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-haze-200">{user?.email}</p>
              <p className="mt-0.5 text-2xs text-haze-400">Signed in</p>
            </div>
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={onLogout}>Log out</Button>
          </div>
        </div>
      </aside>

      <div className="min-w-0 flex-1 lg:pl-[258px]">
        <header className="sticky top-0 z-30 border-b border-ink-600 bg-ink-850/90 backdrop-blur-xl lg:hidden">
          <div className="flex h-14 items-center gap-3 px-4">
            <NavLink to="/admin" className="flex min-w-0 items-center gap-2.5">
              <BrandWordmark className="h-6" />
            </NavLink>
            <div className="ml-auto flex items-center gap-2"><ThemeToggle /><Button variant="ghost" size="sm" onClick={onLogout}>Log out</Button></div>
          </div>
          <nav className="flex gap-1 overflow-x-auto px-3 pb-2" aria-label="Main navigation">
            {nav.map((item) => <NavLink key={item.to} to={item.to} end={item.end}
              className={({ isActive }) => cn("whitespace-nowrap rounded-lg px-3 py-2 text-xs font-medium",
                isActive ? "bg-accent/10 text-accent" : "text-haze-300 hover:bg-ink-700")}>
              {item.label}
            </NavLink>)}
          </nav>
        </header>
        <header className="hidden h-[68px] items-center justify-between border-b border-ink-600/80 px-8 lg:flex">
          <div>
            <p className="text-xs font-medium text-haze-400">Event operations</p>
            <p className="mt-0.5 text-sm font-semibold text-haze-100">{organization?.name ?? "JebaXP Console"}</p>
          </div>
          <span className="flex items-center gap-2 rounded-full border border-ink-600 bg-ink-800 px-3 py-1.5 text-xs text-haze-300">
            <span className="h-2 w-2 rounded-full bg-accent shadow-[0_0_12px_rgb(12_212_158_/_0.6)]" /> Console ready
          </span>
        </header>
        <main className="mx-auto w-full max-w-[1500px] px-4 py-6 sm:px-6 sm:py-8 xl:px-9">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
